"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LegalDisclaimer() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const portrait1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const portrait2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const paragraphs = [{
    text: "InfinitySecure operates with a commitment to transparency and digital integrity. Our legal framework ensures that every client interaction, from initial vulnerability assessment to incident response, is governed by strict confidentiality protocols and industry-standard security practices."
  }, {
    text: "We prioritize the protection of your proprietary data above all else. Our service agreements are designed to provide clear expectations regarding liability, data handling, and the scope of our AI-driven security monitoring services, ensuring your business remains compliant with global cybersecurity regulations."
  }, {
    text: "By engaging with our services, you acknowledge that cybersecurity is a shared responsibility. While our AI-powered tools provide proactive threat prevention, we work in partnership with your internal teams to maintain a robust defense posture against evolving digital threats."
  }, {
    text: "Our commitment to your security is ongoing. We regularly update our terms of service to reflect the latest advancements in threat detection and compliance standards, ensuring that your organization stays protected in an ever-changing digital landscape."
  }];
  return <section data-section-id="1805" id="legaldisclaimer" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 relative">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute top-12 left-4 md:left-12 select-none font-default leading-none" style={{
        fontSize: "clamp(12rem, 22vw, 21rem)",
        color: "var(--foreground)",
        opacity: 0.04
      }}>
          02
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 relative">
          {}
          <div className="lg:col-span-8 relative">
            {}
            <div ref={eyebrow.ref} className={`flex items-center gap-4 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-default uppercase" style={{
              fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
              letterSpacing: "0.22em",
              color: "var(--primary)",
              lineHeight: 1
            }}>
                § 02 / LEGAL — TERMS & COMPLIANCE
              </span>
              <div className="hidden sm:block h-px flex-1 max-w-[240px]" style={{
              background: "linear-gradient(to right, var(--primary), transparent)"
            }} />
              <span aria-hidden="true" className="hidden sm:inline-block w-1.5 h-1.5 rounded-lg" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            {}
            <h1 ref={heading.ref} className={`font-default font-semibold mt-8 transition-all duration-1000 ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
            letterSpacing: "-0.025em",
            lineHeight: 1.02
          }}>
              Our commitment to{" "}
              <span className={`transition-opacity duration-700 delay-200 ${heading.isVisible ? "opacity-100" : "opacity-0"}`} style={{
              color: "var(--primary)"
            }}>
                transparency
              </span>{" "}
              and security.
            </h1>

            {}
            <div className="flex items-center gap-3 mt-10 mb-12">
              <div className="h-px w-40" style={{
              background: "linear-gradient(to right, var(--primary), transparent)"
            }} />
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            {}
            <div className="space-y-10" style={{
            maxWidth: "58ch"
          }}>
              {paragraphs.slice(0, 2).map((p, i) => <ProseBlock key={i} index={i} text={p.text} showDivider={i < 1} />)}

              {}
              <PullQuote text="Security is a shared responsibility. We provide the tools, you provide the vision, and together we secure the future." />

              {paragraphs.slice(2).map((p, i) => <ProseBlock key={i + 2} index={i + 2} text={p.text} showDivider={i < paragraphs.slice(2).length - 1} />)}
            </div>

            {}
            <div className="mt-16 flex items-center gap-4">
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-default uppercase" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.14em",
              color: "var(--muted-foreground)"
            }}>
                InfinitySecure · Legal Framework · Updated 2026
              </span>
            </div>
          </div>

          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-10">
              {}
              <div ref={portrait1.ref} data-index={0} className={`relative transition-all duration-1000 ease-out ${portrait1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <div className="relative overflow-hidden">
                  <div className="relative" style={{
                  aspectRatio: "4 / 5"
                }}>
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/security_compliance_audit.png" alt="Security professional reviewing compliance documentation" loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg shadow-lg" />
                    {}
                    <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(circle at 50% 45%, hsla(185, 100%, 50%, 0.18) 0%, transparent 65%)"
                  }} />
                  </div>
                </div>

                {}
                <div className="mt-4 flex items-center gap-3">
                  <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg shrink-0" style={{
                  backgroundColor: "var(--primary)"
                }} />
                  <span className="font-default uppercase" style={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.14em",
                  color: "var(--muted-foreground)",
                  lineHeight: 1.4
                }}>
                    Compliance Audit · Data Integrity
                  </span>
                </div>
              </div>

              {}
              <div ref={portrait2.ref} data-index={1} className={`relative transition-all duration-1000 ease-out delay-150 ${portrait2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <div className="relative overflow-hidden ml-0 lg:ml-8">
                  <div className="relative" style={{
                  aspectRatio: "3 / 4"
                }}>
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_threat_monitoring.webp" alt="AI-powered threat monitoring dashboard" loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg shadow-lg" />
                    <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(circle at 40% 60%, hsla(185, 100%, 50%, 0.18) 0%, transparent 65%)"
                  }} />
                  </div>
                </div>

                <div className="mt-4 ml-0 lg:ml-8 flex items-center gap-3">
                  <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg shrink-0" style={{
                  backgroundColor: "var(--primary)"
                }} />
                  <span className="font-default uppercase" style={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.14em",
                  color: "var(--muted-foreground)",
                  lineHeight: 1.4
                }}>
                    Proactive Monitoring · 24/7 Protection
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ProseBlock({
  index,
  text,
  showDivider
}: {
  index: number;
  text: string;
  showDivider: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${index % 3 * 80}ms`
  }}>
      <p className="font-default" style={{
      fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
      lineHeight: 1.65,
      color: "var(--foreground)",
      fontWeight: 400
    }}>
        {text}
      </p>

      {showDivider && <div className="flex items-center gap-3 mt-10">
          <div className="h-px w-32" style={{
        background: "linear-gradient(to right, var(--primary), transparent)"
      }} />
          <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg" style={{
        backgroundColor: "var(--primary)"
      }} />
        </div>}
    </div>;
}
function PullQuote({
  text
}: {
  text: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} className={`relative pl-8 py-4 my-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    borderLeft: "2px solid var(--primary)"
  }}>
      <blockquote className="font-default italic" style={{
      fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
      color: "var(--foreground)",
      fontWeight: 300
    }}>
        “{text}”
      </blockquote>
    </div>;
}